// InternationalProducts.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import * as THREE from "three";
import { feature } from "topojson-client";
import worldData from "world-atlas/countries-110m.json";

import "./styles/internationalProducts.css";

/*
=========================================================
  DESTINATIONS
=========================================================
*/

const destinations = [
  {
    country: "USA",
    city: "New York",
    lat: 39.8283,
    lng: -98.5795,
    code: "US",
  },
  {
    country: "Canada",
    city: "Toronto",
    lat: 56.1304,
    lng: -106.3468,
    code: "CA",
  },
  {
    country: "Mexico",
    city: "Mexico City",
    lat: 23.6345,
    lng: -102.5528,
    code: "MX",
  },
  {
    country: "Japan",
    city: "Tokyo",
    lat: 36.2048,
    lng: 138.2529,
    code: "JP",
  },
  {
    country: "Australia",
    city: "Sydney",
    lat: -25.2744,
    lng: 133.7751,
    code: "AU",
  },
  {
    country: "New Zealand",
    city: "Auckland",
    lat: -41.0,
    lng: 174.0,
    code: "NZ",
  },
];


/*
=========================================================
  SCOTLAND
=========================================================
*/

const SCOTLAND = {
  country: "Scotland",
  city: "Glasgow",
  lat: 55.8642,
  lng: -4.2518,
  code: "GB",
};

const getFlagUrl = (code) => {
  const flagCode = code === "AS" ? "cn" : code.toLowerCase();

  return `https://flagcdn.com/w40/${flagCode}.png`;
};

/*
=========================================================
  COUNTRY POLYGONS
=========================================================
*/

const countries = feature(
  worldData,
  worldData.objects.countries
).features;

/*
=========================================================
  PIN COMPONENT
=========================================================
*/

function createPinElement(country, type) {
  const wrapper = document.createElement("div");

  wrapper.className = `
    world-pin
    ${type === "destination" ? "world-pin-scotland" : ""}
  `;

  wrapper.innerHTML = `
    <div class="world-pin-pulse"></div>

    <div class="world-pin-icon">
      <svg width="30" height="38" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M16 39C13 34 3 24 3 15A13 13 0 0 1 29 15C29 24 19 34 16 39Z" fill="currentColor" stroke="white" stroke-width="2" stroke-linejoin="round" />
        <circle cx="16" cy="15" r="6" fill="white" />
      </svg>
    </div>

    <div class="world-pin-label">
      ${country}
    </div>
  `;

  return wrapper;
}

/*
=========================================================
  COMPONENT
=========================================================
*/

export default function InternationalProducts() {
  const globeRef = useRef(null);
  const globeStageRef = useRef(null);
  const countrySelectedRef = useRef(false);
  const globeInViewRef = useRef(true);

  const resumeVisibleGlobe = useCallback(() => {
    const globe = globeRef.current;
    if (!globe || !globeInViewRef.current || document.hidden) return;
    // Route lines need continuous rendering, even when the globe is stationary.
    globe.resumeAnimation();
    if (globeStageRef.current) globeStageRef.current.dataset.globeAnimating = "true";
  }, []);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 700);

  const [activeCountry, setActiveCountry] = useState(null);

  /*
  =======================================================
    GLOBE MATERIAL
  =======================================================
  */

  const globeMaterial = useMemo(() => {
    return new THREE.MeshPhongMaterial({
      color: "#dceeed",
      transparent: true,
      opacity: 0.95,
      shininess: 0,
      flatShading: true,
    });
  }, []);

  /*
  =======================================================
    COUNTRY POLYGON STYLE
  =======================================================
  */

  const polygonAltitude = 0.012;

  /*
  =======================================================
    ROUTES
  =======================================================
  */

  const arcs = useMemo(() => {
    return destinations.map((location) => ({
      startLat: location.lat,
      startLng: location.lng,

      endLat: SCOTLAND.lat,
      endLng: SCOTLAND.lng,

      country: location.country,
    }));
  }, []);

  /*
  =======================================================
    POINTS
  =======================================================
  */

  const points = useMemo(() => {
    return [
      ...destinations.map((location) => ({
        ...location,
        type: "origin",
      })),

      {
        ...SCOTLAND,
        type: "destination",
      },
    ];
  }, []);

  /*
  =======================================================
    RINGS
  =======================================================
  */

  const rings = useMemo(() => {
    return points.map((point) => ({
      ...point,
      maxR: point.type === "destination" ? 4.8 : 2.8,
    }));
  }, [points]);

  /*
  =======================================================
    CONTROLS
  =======================================================
  */

  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return undefined;
    const controls = globe.controls();
    controls.autoRotate = window.innerWidth > 700;
    controls.autoRotateSpeed = 0.22;
    controls.enablePan = false;
    controls.minDistance = 170;
    controls.maxDistance = 360;

    const handleResize = () => {
      const mobile = window.innerWidth <= 700;
      setIsMobile(mobile);
      controls.autoRotate = !mobile && !countrySelectedRef.current;
      controls.enableRotate = !mobile;
      controls.enableZoom = !mobile;
      // Reduce the drawing buffer, without shrinking the globe or its HTML pins.
      globe.renderer().setPixelRatio(mobile ? 0.8 : Math.min(2, window.devicePixelRatio || 1));
      resumeVisibleGlobe();
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    globe.pointOfView({ lat: 32, lng: 8, altitude: 2.15 }, 1200);

    return () => window.removeEventListener("resize", handleResize);
  }, [resumeVisibleGlobe]);

  useEffect(() => {
    const globe = globeRef.current;
    const stage = globeStageRef.current;
    if (!globe || !stage) return undefined;
    const updateRendering = () => {
      if (globeInViewRef.current && !document.hidden) resumeVisibleGlobe();
      else {
        globe.pauseAnimation();
        stage.dataset.globeAnimating = "false";
      }
    };
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(
      ([entry]) => {
        globeInViewRef.current = entry.isIntersecting;
        updateRendering();
      },
      { rootMargin: "100px" }
    );
    observer?.observe(stage);
    document.addEventListener("visibilitychange", updateRendering);
    updateRendering();
    return () => {
      observer?.disconnect();
      document.removeEventListener("visibilitychange", updateRendering);
    };
  }, [resumeVisibleGlobe]);

  /*
  =======================================================
    SELECTED LOCATION
  =======================================================
  */

  const focusCountry = (location) => {
    setActiveCountry(location);
    countrySelectedRef.current = true;
    const globe = globeRef.current;
    if (!globe) return;

    // Pause the idle spin so the selected country stays in front.
    globe.controls().autoRotate = false;
    resumeVisibleGlobe();
    globe.pointOfView(
      { lat: location.lat, lng: location.lng },
      globeInViewRef.current && !document.hidden ? 1200 : 0
    );
  };

  const selectedLocation = activeCountry;

  return (
    <section className="international-products">

      {/* BACKGROUND */}

      <div className="international-orb international-orb-pink" />
      <div className="international-orb international-orb-teal" />
      <div className="international-grid-pattern" />

      <div className="international-products-inner">

        {/* =================================================
            TOP BAR
        ================================================= */}

        <div className="international-topbar">

          <div className="international-topbar-brand">
            <span className="international-topbar-dot" />
            INTERNATIONAL
          </div>

          <div className="international-topbar-line" />

          <div className="international-topbar-count">
            06 DESTINATIONS
          </div>

        </div>

        {/* =================================================
            HERO
        ================================================= */}

        <header className="international-hero">

          {/* COPY */}

          <div className="international-hero-copy">

            <div className="international-kicker">
              <span />
              FLAVOURS WITHOUT BORDERS
            </div>

            <h1>
              The world
              <br />
              <em>comes to</em>
              <br />
              TUBBEES.
            </h1>

            <p className="international-hero-description">
              From iconic American sweets to treats discovered across Asia
              and beyond, Tubbees brings a little piece of everywhere home.
            </p>

            <div className="international-hero-footer">

              <div className="international-scroll-indicator">
                <span className="international-scroll-line" />
                EXPLORE THE JOURNEY
              </div>

            </div>

          </div>

          {/* =================================================
              GLOBE
          ================================================= */}

          <div
            className="international-globe-stage"
            ref={globeStageRef}
            onPointerDownCapture={() => resumeVisibleGlobe()}
            onPointerMoveCapture={(event) => { if (event.buttons) resumeVisibleGlobe(); }}
            onWheelCapture={() => resumeVisibleGlobe()}
          >

            <div className="international-globe-halo" />

            <div className="international-globe-orbit orbit-one" />
            <div className="international-globe-orbit orbit-two" />

            {/* GLOBE */}

            <div className="international-globe">

              <Globe
                ref={globeRef}
                animateIn={!isMobile}
                onGlobeReady={() => resumeVisibleGlobe()}

                width={1200}
                height={1000}

                backgroundColor="rgba(0,0,0,0)"

                globeMaterial={globeMaterial}

                showAtmosphere={!isMobile}
                globeCurvatureResolution={isMobile ? 6 : 4}
                atmosphereColor="#69c7c5"
                atmosphereAltitude={0.16}

                /*
                ---------------------------------------------
                  COUNTRY SHAPES
                ---------------------------------------------
                */

                polygonsData={countries}

                polygonAltitude={polygonAltitude}
                polygonCapCurvatureResolution={isMobile ? 8 : 5}

                polygonCapColor={(d) => {
                  const name = d.properties?.name || "";
                  const isDestination = destinations.some((location) =>
                    (location.code === "US" ? "United States of America" : location.country) === name
                  );

                  if (isDestination) return "#f7e1e8";
                  if (name === "United Kingdom") return "#69c7c5";
                  return "#eef6f5";
                }}

                polygonSideColor={() =>
                  "rgba(105,199,197,0.18)"
                }

                polygonStrokeColor={() =>
                  "rgba(37,51,66,0.20)"
                }

                polygonLabel={(d) => {
                  return `
                    <div class="globe-country-tooltip">
                      ${d.properties?.name || "Country"}
                    </div>
                  `;
                }}

                polygonsTransitionDuration={500}

                /*
                ---------------------------------------------
                  ROUTES
                ---------------------------------------------
                */

                arcsData={arcs}

                arcStartLat={(d) => d.startLat}
                arcStartLng={(d) => d.startLng}

                arcEndLat={(d) => d.endLat}
                arcEndLng={(d) => d.endLng}

                arcColor={() => "#f3a0bd"}

                arcAltitudeAutoScale={0.65}

                arcStroke={0.85}
                arcCurveResolution={isMobile ? 24 : 64}
                arcCircularResolution={isMobile ? 3 : 6}

                arcDashLength={0.22}
                arcDashGap={0.8}

                arcDashInitialGap={(d) => {
                  const index = destinations.findIndex(
                    (item) => item.country === d.country
                  );

                  return index * 0.2;
                }}

                arcDashAnimateTime={2400}

                /*
                ---------------------------------------------
                  POINTS
                ---------------------------------------------
                */

                pointsData={points}

                pointLat={(d) => d.lat}
                pointLng={(d) => d.lng}

                pointColor={(d) =>
                  d.type === "destination"
                    ? "#69c7c5"
                    : "#f3a0bd"
                }

                pointAltitude={0}

                pointRadius={(d) =>
                  d.type === "destination"
                    ? 0.55
                    : 0.28
                }

                pointsMerge={false}

                onPointClick={focusCountry}

                /*
                ---------------------------------------------
                  PULSING RINGS
                ---------------------------------------------
                */

                ringsData={isMobile ? [] : rings}

                ringLat={(d) => d.lat}
                ringLng={(d) => d.lng}

                ringColor={(d) =>
                  d.type === "destination"
                    ? () => "#69c7c5"
                    : () => "#f3a0bd"
                }

                ringMaxRadius={(d) => d.maxR}

                ringPropagationSpeed={1.15}
                ringRepeatPeriod={1900}

                /*
                ---------------------------------------------
                  CUSTOM LOCATION PINS
                ---------------------------------------------
                */

                htmlElementsData={points}

                htmlLat={(d) => d.lat}
                htmlLng={(d) => d.lng}

                htmlAltitude={polygonAltitude + 0.002}

                htmlElement={(d) =>
                  createPinElement(
                    d.country,
                    d.type
                  )
                }
              />

            </div>

            {/* =================================================
                LOCATION CARD
            ================================================= */}

            {selectedLocation && (
            <div className="international-location-card">

              <div className="location-card-top">

                <span>
                  CURRENT CONNECTION
                </span>

                <span className="location-card-code">
                  <img
                    src={getFlagUrl(selectedLocation.code)}
                    alt={`${selectedLocation.country} flag`}
                  />
                </span>

              </div>

              <div className="location-card-main">

                <div className="location-card-pulse" />

                <div>

                  <small>
                    {selectedLocation.country === SCOTLAND.country ? "TUBBEES HOME" : "INTERNATIONAL ORIGIN"}
                  </small>

                  <strong>
                    {selectedLocation.country}
                  </strong>

                </div>

              </div>

              <div className="location-card-route">
                {selectedLocation.country === SCOTLAND.country ? "Glasgow, Scotland" : `${selectedLocation.city} → Glasgow`}
              </div>

            </div>
            )}

            {/* =================================================
                LEGEND
            ================================================= */}

            <div className="international-globe-key">

              <div>
                <i className="key-pink" />
                WORLD
              </div>

              <div>
                <i className="key-teal" />
                SCOTLAND
              </div>

            </div>

          </div>

        </header>

        {/* =================================================
            DESTINATIONS
        ================================================= */}

        <div className="international-destinations">

          <div className="international-destination-intro">

            <span>01</span>

            <p>
              WHERE OUR
              <br />
              FLAVOURS BEGIN
            </p>

          </div>

          <div className="international-destination-list">

            {destinations.map((location, index) => (

              <button
                key={location.country}
                type="button"

                className={`
                  international-destination
                  ${
                    activeCountry?.country === location.country
                      ? "is-active"
                      : ""
                  }
                `}

                onClick={() => focusCountry(location)}
              >

                <span className="destination-number">
                  0{index + 1}
                </span>

                <span className="destination-country">
                  <img
                    className="destination-flag"
                    src={getFlagUrl(location.code)}
                    alt={`${location.country} flag`}
                  />
                  {location.country}
                </span>

                <span className="destination-city">
                  {location.city}
                </span>

                <span className="destination-arrow">
                  ↗
                </span>

              </button>

            ))}

          </div>

        </div>

        {/* =================================================
            FOOTER
        ================================================= */}

        <footer className="international-bottom">

          <div className="international-bottom-statement">

            <span className="statement-line" />

            <p>
              Different countries.
              <br />
              <strong>
                One sweet destination.
              </strong>
            </p>

          </div>

          <div className="international-bottom-stat">

            <strong>∞</strong>

            <span>
              FLAVOURS
              <br />
              WITHOUT BORDERS
            </span>

          </div>

          <div className="international-bottom-location">

            <span className="bottom-location-dot" />

            SCOTLAND

          </div>

        </footer>

      </div>

    </section>
  );
}